import { useState } from 'react';
import { SOCIAL_ASSET_CHANNELS, type SocialAssetWithProject } from '../../../shared/types';
import { DP_DOW, monthGrid, shiftMonth as shiftYearMonth } from '../../lib/calendar';
import { CHANNEL_LABELS, MONTHS_FULL, scheduleSheetLabel, toggleChannel, type ScheduleDraft } from '../../lib/socialCalendar';
import { Sheet, SheetButton, SheetSection } from '../Sheet';

// ── Schedule sheet (mockup §10.1 D) ──────────────────────────────────────

export function ScheduleSheet({
  draft,
  assets,
  today,
  onChange,
  onConfirm,
  onClose,
}: {
  draft: ScheduleDraft;
  assets: SocialAssetWithProject[];
  today: string;
  onChange: (next: ScheduleDraft) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [view, setView] = useState({ y: Number(draft.date.slice(0, 4)), m: Number(draft.date.slice(5, 7)) });
  // Days that already carry a slot get a dot — the mockup's `.m` cell. The
  // asset being placed is excluded: its own current date is not a clash.
  const taken = new Set(
    assets
      .filter((a) => a.id !== draft.asset.id && a.planned_date !== null && !a.effective_restricted)
      .map((a) => a.planned_date as string),
  );

  return (
    <Sheet
      open
      onClose={onClose}
      variant="half"
      title="Schedule"
      context={draft.asset.label}
      rightAction={<SheetButton onClick={onClose}>Cancel</SheetButton>}
      className="social-m-sheet"
      footer={
        <button
          type="button"
          className="cbtn wide pri"
          disabled={draft.channels.length === 0}
          onClick={onConfirm}
        >
          {scheduleSheetLabel(draft.channels, draft.date)}
        </button>
      }
    >
      <SheetSection>Channels</SheetSection>
      <div className="chiprow">
        {SOCIAL_ASSET_CHANNELS.map((c) => (
          <button
            type="button"
            key={c}
            className={`c${draft.channels.includes(c) ? ' on' : ''}`}
            aria-pressed={draft.channels.includes(c)}
            onClick={() => onChange({ ...draft, channels: toggleChannel(draft.channels, c) })}
          >
            {CHANNEL_LABELS[c]}
          </button>
        ))}
      </div>
      <div className="cal">
        <div className="cal-head">
          <span className="cal-month">
            {MONTHS_FULL[view.m - 1]} {view.y}
          </span>
          <span className="cal-nav">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setView(({ y, m }) => { const n = shiftYearMonth(y, m, -1); return { y: n.year, m: n.month }; })}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setView(({ y, m }) => { const n = shiftYearMonth(y, m, 1); return { y: n.year, m: n.month }; })}
            >
              ›
            </button>
          </span>
        </div>
        <div className="cal-grid">
          {DP_DOW.map((d) => (
            <span className="cal-dow" key={d}>
              {d}
            </span>
          ))}
          {monthGrid(view.y, view.m).map((iso) => {
            const cls = [
              'cal-day',
              Number(iso.slice(5, 7)) !== view.m ? 'out' : '',
              iso === today ? 'today' : '',
              iso === draft.date ? 'sel' : '',
              taken.has(iso) ? 'social-m-marked' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                type="button"
                className={cls}
                key={iso}
                aria-pressed={iso === draft.date}
                onClick={() => onChange({ ...draft, date: iso })}
              >
                {Number(iso.slice(8, 10))}
              </button>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}
